import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { dbHelpers } from "../lib/db";
import { Album, Playlist, RadioStation, Track } from "../types";

interface LikedContent {
  id: string;
  type: "track" | "album" | "playlist" | "radio";
  source: "local" | "spotify";
  timestamp: number;
}

interface LocalLibraryState {
  initialized: boolean;
  likedContent: LikedContent[];
  albums: Album[];
  playlists: Playlist[];
  radioStations: RadioStation[];
  recentlyPlayed: Track[];
  localTracks: Track[];

  initialize: () => Promise<void>;
  isContentLiked: (id: string, type: LikedContent["type"]) => boolean;
  toggleLike: (id: string, type: LikedContent["type"], source: "local" | "spotify") => void;
  getLikedContent: (type: LikedContent["type"]) => LikedContent[];
  getTrack: (id: string) => Promise<Track | undefined>;
  addAlbum: (name: string, artist: string, description?: string, coverUrl?: string) => Promise<string>;
  updateAlbum: (id: string, updates: Partial<Album>) => void;
  deleteAlbum: (id: string) => void;
  addTrackToAlbum: (albumId: string, track: Track) => void;
  removeTrackFromAlbum: (albumId: string, trackId: string) => void;
  getAllAlbums: () => Album[];
  createPlaylist: (name: string, description?: string, icon?: string) => string;
  updatePlaylist: (id: string, updates: Partial<Playlist>) => void;
  deletePlaylist: (id: string) => void;
  addTrackToPlaylist: (playlistId: string, track: Track) => void;
  removeTrackFromPlaylist: (playlistId: string, trackId: string) => void;
  getAllPlaylists: () => Playlist[];
  createRadioStation: (name: string, description?: string, coverUrl?: string) => string;
  updateRadioStation: (id: string, updates: Partial<RadioStation>) => void;
  deleteRadioStation: (id: string) => void;
  addTrackToRadioStation: (stationId: string, track: Track) => void;
  removeTrackFromRadioStation: (stationId: string, trackId: string) => void;
  getAllRadioStations: () => RadioStation[];
  addToRecentlyPlayed: (track: Track) => void;
  getRecentlyPlayed: () => Track[];
  clearRecentlyPlayed: () => void;
  updateTrackMetadata: (trackId: string, updates: Partial<Track>) => void;
  syncLocalFiles: (files: Track[]) => void;
  addLocalTrack: (track: Track) => void;
  removeLocalTrack: (id: string) => void;
  getAllTracks: () => Track[];
}

export const useLocalLibraryStore = create<LocalLibraryState>((set, get) => ({
  initialized: false,
  likedContent: [],
  albums: [],
  playlists: [],
  radioStations: [],
  recentlyPlayed: [],
  localTracks: [],

  getAllTracks: () => {
    const state = get();
    const trackMap = new Map<string, Track>();

    // Add local tracks
    state.localTracks.forEach((track) => trackMap.set(track.id, track));

    // Add tracks from albums
    state.albums.forEach((album) =>
      album.tracks.forEach((track) => {
        if (!trackMap.has(track.id)) {
          trackMap.set(track.id, track);
        }
      })
    );

    // Add tracks from playlists
    state.playlists.forEach((playlist) =>
      playlist.tracks.forEach((track) => {
        if (!trackMap.has(track.id)) {
          trackMap.set(track.id, track);
        }
      })
    );

    // Add tracks from radio stations
    state.radioStations.forEach((station) =>
      station.tracks.forEach((track) => {
        if (!trackMap.has(track.id)) {
          trackMap.set(track.id, track);
        }
      })
    );

    return Array.from(trackMap.values());
  },

  initialize: async () => {
    if (get().initialized) return;

    console.log("Starting local library initialization...");

    try {
      const [likedContent, albums, playlists, radioStations, recentlyPlayed, localTracks] = await Promise.all([
        dbHelpers.getLikedContent().catch(() => []),
        dbHelpers.getAllAlbums().catch(() => []),
        dbHelpers.getAllPlaylists().catch(() => []),
        dbHelpers.getAllRadioStations().catch(() => []),
        dbHelpers.getRecentlyPlayed().catch(() => []),
        dbHelpers.getAllTracks().catch(() => []),
      ]);

      console.log("Library data loaded:", {
        likedContent: likedContent.length,
        albums: albums.length,
        playlists: playlists.length,
        radioStations: radioStations.length,
        recentlyPlayed: recentlyPlayed.length,
        localTracks: localTracks.length,
      });

      const regularPlaylists = playlists.filter((playlist) => !playlist.isAlbum);

      // Only add demo content if the library is completely empty
      if (localTracks.length === 0 && albums.length === 0) {
        console.log("Library is empty, adding demo content...");
        try {
          const response = await fetch("/demo/tracks.json");
          if (response.ok) {
            const demoData = await response.json();

            // Add demo tracks to localTracks array
            const demoLocalTracks = demoData.tracks || [];
            for (const track of demoLocalTracks) {
              try {
                await dbHelpers.addTrack(track);
                localTracks.push(track);
              } catch (error) {
                console.warn("Failed to add demo track:", track.id, error);
              }
            }

            // Add demo album
            const demoAlbum = demoData.albums?.[0];
            if (demoAlbum) {
              try {
                await dbHelpers.addAlbum(demoAlbum);
                albums.push(demoAlbum);
              } catch (error) {
                console.warn("Failed to add demo album:", demoAlbum.id, error);
              }
            }

            console.log("Demo content added successfully");
          } else {
            console.warn("Failed to fetch demo tracks:", response.status);
          }
        } catch (demoError) {
          console.warn("Failed to load demo tracks:", demoError);
        }
      }

      set({
        likedContent,
        albums,
        playlists: regularPlaylists,
        radioStations,
        recentlyPlayed,
        localTracks,
        initialized: true,
      });

      console.log("Local library initialization completed successfully");
    } catch (error) {
      console.error("Error loading library:", error);
      // Set initialized to true even on error to prevent infinite loading
      set({ initialized: true });
    }
  },

  getTrack: async (id: string) => {
    try {
      const track = await dbHelpers.getTrack(id);
      return track;
    } catch (error) {
      console.error("Error getting track:", error);
      return undefined;
    }
  },

  isContentLiked: (id, type) => {
    return get().likedContent.some((item) => item.id === id && item.type === type);
  },

  toggleLike: async (id, type, source) => {
    set((state) => {
      const isLiked = state.likedContent.some((item) => item.id === id && item.type === type);

      const newState = isLiked
        ? {
            likedContent: state.likedContent.filter((item) => !(item.id === id && item.type === type)),
          }
        : {
            likedContent: [
              ...state.likedContent,
              {
                id,
                type,
                source,
                timestamp: Date.now(),
              },
            ],
          };

      // Persist to IndexedDB
      if (isLiked) {
        dbHelpers.removeLikedContent(id).catch(console.error);
      } else {
        dbHelpers
          .addLikedContent({
            id,
            type,
            source,
            timestamp: Date.now(),
          })
          .catch(console.error);
      }

      return newState;
    });
  },

  getLikedContent: (type) => {
    return get().likedContent.filter((item) => item.type === type);
  },

  addAlbum: async (name: string, artist: string, description?: string, coverUrl?: string) => {
    const id = uuidv4();
    const now = Date.now();
    const album: Album = {
      id,
      name,
      artist,
      description,
      coverUrl,
      tracks: [],
      source: "local",
      createdAt: now,
      updatedAt: now,
      genre: "",
      year: "",
      tags: [],
      isAlbum: true,
    };

    // First update the local state
    set((state) => ({
      albums: [...state.albums, album],
    }));

    // Then persist to IndexedDB
    try {
      await dbHelpers.addAlbum(album);
    } catch (error) {
      console.error("Error persisting album:", error);
      // Optionally roll back the state update if persistence fails
      set((state) => ({
        albums: state.albums.filter((a) => a.id !== id),
      }));
      throw error;
    }

    return id;
  },

  updateAlbum: async (id, updates) => {
    set((state) => {
      const newState = {
        albums: state.albums.map((album) =>
          album.id === id
            ? {
                ...album,
                ...updates,
                updatedAt: Date.now(),
              }
            : album
        ),
      };

      // Persist to IndexedDB using addAlbum helper
      const updatedAlbum = newState.albums.find((a) => a.id === id);
      if (updatedAlbum) {
        dbHelpers.addAlbum(updatedAlbum).catch(console.error);
      }

      return newState;
    });
  },

  deleteAlbum: async (id) => {
    await dbHelpers.deletePlaylist(id);
    set((state) => ({
      albums: state.albums.filter((album) => album.id !== id),
    }));
  },

  addTrackToAlbum: async (albumId, track) => {
    set((state) => {
      const newState = {
        albums: state.albums.map((album) =>
          album.id === albumId
            ? {
                ...album,
                tracks: [...album.tracks, track],
              }
            : album
        ),
      };

      // Persist to IndexedDB using addAlbum helper
      const updatedAlbum = newState.albums.find((a) => a.id === albumId);
      if (updatedAlbum) {
        dbHelpers.addAlbum(updatedAlbum).catch(console.error);
      }

      return newState;
    });
  },

  removeTrackFromAlbum: async (albumId, trackId) => {
    set((state) => {
      const newState = {
        albums: state.albums.map((album) =>
          album.id === albumId
            ? {
                ...album,
                tracks: album.tracks.filter((track) => track.id !== trackId),
              }
            : album
        ),
      };

      // Persist to IndexedDB using addAlbum helper
      const updatedAlbum = newState.albums.find((a) => a.id === albumId);
      if (updatedAlbum) {
        dbHelpers.addAlbum(updatedAlbum).catch(console.error);
      }

      return newState;
    });
  },

  getAllAlbums: () => {
    return get().albums;
  },

  createPlaylist: (name, description, icon) => {
    const id = uuidv4();
    const playlist: Playlist = {
      id,
      name,
      description,
      icon,
      tracks: [],
      source: "local",
      isAlbum: false,
    };

    set((state) => ({
      playlists: [...state.playlists, playlist],
    }));

    // Persist to IndexedDB
    dbHelpers.addPlaylist(playlist).catch(console.error);

    return id;
  },

  updatePlaylist: async (id, updates) => {
    set((state) => {
      const newState = {
        playlists: state.playlists.map((playlist) => (playlist.id === id ? { ...playlist, ...updates } : playlist)),
      };

      // Persist to IndexedDB
      const updatedPlaylist = newState.playlists.find((p) => p.id === id);
      if (updatedPlaylist) {
        dbHelpers.addPlaylist(updatedPlaylist).catch(console.error);
      }

      return newState;
    });
  },

  deletePlaylist: async (id) => {
    await dbHelpers.deletePlaylist(id);
    set((state) => ({
      playlists: state.playlists.filter((playlist) => playlist.id !== id),
    }));
  },

  addTrackToPlaylist: async (playlistId, track) => {
    set((state) => {
      const newState = {
        playlists: state.playlists.map((playlist) =>
          playlist.id === playlistId
            ? {
                ...playlist,
                tracks: [...playlist.tracks, track],
              }
            : playlist
        ),
      };

      // Persist to IndexedDB
      const updatedPlaylist = newState.playlists.find((p) => p.id === playlistId);
      if (updatedPlaylist) {
        dbHelpers.addPlaylist(updatedPlaylist).catch(console.error);
      }

      return newState;
    });
  },

  removeTrackFromPlaylist: async (playlistId, trackId) => {
    set((state) => {
      const newState = {
        playlists: state.playlists.map((playlist) =>
          playlist.id === playlistId
            ? {
                ...playlist,
                tracks: playlist.tracks.filter((track) => track.id !== trackId),
              }
            : playlist
        ),
      };

      // Persist to IndexedDB
      const updatedPlaylist = newState.playlists.find((p) => p.id === playlistId);
      if (updatedPlaylist) {
        dbHelpers.addPlaylist(updatedPlaylist).catch(console.error);
      }

      return newState;
    });
  },

  getAllPlaylists: () => {
    return get().playlists;
  },

  createRadioStation: (name, description, coverUrl) => {
    const id = uuidv4();
    const radioStation: RadioStation = {
      id,
      name,
      description,
      coverUrl,
      tracks: [],
      source: "local",
      trackCount: 0,
      createdAt: Date.now(),
    };

    set((state) => ({
      radioStations: [...state.radioStations, radioStation],
    }));

    // Persist to IndexedDB
    dbHelpers.addRadioStation(radioStation).catch(console.error);

    return id;
  },

  updateRadioStation: async (id, updates) => {
    set((state) => {
      const newState = {
        radioStations: state.radioStations.map((station) => (station.id === id ? { ...station, ...updates } : station)),
      };

      // Persist to IndexedDB
      const updatedStation = newState.radioStations.find((s) => s.id === id);
      if (updatedStation) {
        dbHelpers.addRadioStation(updatedStation).catch(console.error);
      }

      return newState;
    });
  },

  deleteRadioStation: async (id) => {
    await dbHelpers.deleteRadioStation(id);
    set((state) => ({
      radioStations: state.radioStations.filter((station) => station.id !== id),
    }));
  },

  addTrackToRadioStation: async (stationId, track) => {
    set((state) => {
      const newState = {
        radioStations: state.radioStations.map((station) =>
          station.id === stationId
            ? {
                ...station,
                tracks: [...station.tracks, track],
                trackCount: station.trackCount + 1,
              }
            : station
        ),
      };

      // Persist to IndexedDB
      const updatedStation = newState.radioStations.find((s) => s.id === stationId);
      if (updatedStation) {
        dbHelpers.addRadioStation(updatedStation).catch(console.error);
      }

      return newState;
    });
  },

  removeTrackFromRadioStation: async (stationId, trackId) => {
    set((state) => {
      const newState = {
        radioStations: state.radioStations.map((station) =>
          station.id === stationId
            ? {
                ...station,
                tracks: station.tracks.filter((track) => track.id !== trackId),
                trackCount: station.trackCount - 1,
              }
            : station
        ),
      };

      // Persist to IndexedDB
      const updatedStation = newState.radioStations.find((s) => s.id === stationId);
      if (updatedStation) {
        dbHelpers.addRadioStation(updatedStation).catch(console.error);
      }

      return newState;
    });
  },

  getAllRadioStations: () => {
    return get().radioStations;
  },

  addToRecentlyPlayed: async (track) => {
    set((state) => {
      const newRecent = [track, ...state.recentlyPlayed.filter((t) => t.id !== track.id)].slice(0, 50); // Keep last 50 tracks

      // Persist to IndexedDB
      dbHelpers.addRecentlyPlayed(track).catch(console.error);

      return { recentlyPlayed: newRecent };
    });
  },

  getRecentlyPlayed: () => {
    return get().recentlyPlayed;
  },

  clearRecentlyPlayed: () => {
    set({ recentlyPlayed: [] });
    // DO NOT REMOVE BELOW COMMENT!!!
    // Note: We're not clearing from IndexedDB here. If needed, add a clearRecentlyPlayed method to dbHelpers.
    // dbHelpers.clearRecentlyPlayed().catch(console.error);
  },

  updateTrackMetadata: async (trackId, updates) => {
    set((state) => {
      const newState = {
        albums: state.albums.map((album) => ({
          ...album,
          tracks: album.tracks.map((track) => (track.id === trackId ? { ...track, ...updates } : track)),
        })),
        playlists: state.playlists.map((playlist) => ({
          ...playlist,
          tracks: playlist.tracks.map((track) => (track.id === trackId ? { ...track, ...updates } : track)),
        })),
        radioStations: state.radioStations.map((station) => ({
          ...station,
          tracks: station.tracks.map((track) => (track.id === trackId ? { ...track, ...updates } : track)),
        })),
        localTracks: state.localTracks.map((track) => (track.id === trackId ? { ...track, ...updates } : track)),
        recentlyPlayed: state.recentlyPlayed.map((track) => (track.id === trackId ? { ...track, ...updates } : track)),
      };

      // Persist updated albums, playlists, radio stations, and local tracks to IndexedDB
      const persistPromises = [
        ...newState.albums
          .filter((album) => album.tracks.some((t) => t.id === trackId))
          .map((album) => dbHelpers.addPlaylist(album as unknown as Playlist)),
        ...newState.playlists
          .filter((playlist) => playlist.tracks.some((t) => t.id === trackId))
          .map((playlist) => dbHelpers.addPlaylist(playlist)),
        ...newState.radioStations
          .filter((station) => station.tracks.some((t) => t.id === trackId))
          .map((station) => dbHelpers.addRadioStation(station)),
      ];

      // Update the track in the local files store
      const existingTrack = state.localTracks.find((track) => track.id === trackId);
      if (existingTrack) {
        persistPromises.push(dbHelpers.addTrack({ ...existingTrack, ...updates }));
      }

      Promise.all(persistPromises).catch((error) => {
        console.error("Error persisting track metadata updates:", error);
      });

      return newState;
    });

    console.log(`Track ${trackId} metadata updated:`, updates);
  },

  syncLocalFiles: (files) => {
    set({ localTracks: files });
  },

  addLocalTrack: (track) => {
    set((state) => ({
      localTracks: [...state.localTracks, track],
    }));
    // Persist to IndexedDB
    dbHelpers.addTrack(track).catch(console.error);
  },

  removeLocalTrack: (id) => {
    set((state) => ({
      localTracks: state.localTracks.filter((track) => track.id !== id),
    }));
    // Remove from IndexedDB
    dbHelpers.deleteTrack(id).catch(console.error);
  },
}));

// Initialize the store immediately with enhanced logging
console.log("Initializing local library store...");
useLocalLibraryStore
  .getState()
  .initialize()
  .then(() => {
    console.log("Local library store initialized successfully");
  })
  .catch((error) => {
    console.error("Local library store initialization failed:", error);
  });

useLocalLibraryStore.subscribe((state) => {
  if (state.initialized) {
    state.localTracks.forEach((track) => {
      dbHelpers.addTrack(track).catch(console.error);
    });
  }
});
